<template>
	<div>
		<!-- 采购入库详情模态框 -->
		<el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="80%" top="8vh"
			class="purchase-detail-dialog" :close-on-click-modal="false" @close="handleClose">
			<div class="detail-content">
				<!-- 加载状态 -->
				<div v-if="loading" class="loading-container">
					<el-skeleton animated :rows="8" class="custom-skeleton">
						<template slot="template">
							<div class="skeleton-header">
								<el-skeleton-item variant="h3" style="width: 200px; height: 24px;" />
								<el-skeleton-item variant="button" style="width: 80px; height: 24px;" />
							</div>
							<div class="skeleton-section">
								<el-skeleton-item variant="h3"
									style="width: 120px; height: 20px; margin-bottom: 16px;" />
								<el-descriptions :column="2" border class="skeleton-descriptions">
									<el-descriptions-item v-for="i in 8" :key="i" :span="1">
										<template slot="label">
											<el-skeleton-item variant="text" style="width: 60px;" />
										</template>
										<el-skeleton-item variant="text" style="width: 100px;" />
									</el-descriptions-item>
								</el-descriptions>
							</div>
							<div class="skeleton-section">
								<el-skeleton-item variant="h3"
									style="width: 120px; height: 20px; margin-bottom: 16px;" />
								<el-skeleton-item variant="table" style="height: 200px;" />
							</div>
						</template>
					</el-skeleton>
				</div>

				<!-- 内容区域 -->
				<div v-else-if="hasLoaded">
					<!-- 顶部信息 -->
					<div class="header-info">
						<div class="drug-name">
							{{ getFirstDrugName() || '采购入库详情' }}
						</div>
						<el-tag :type="statusTagType" size="small">{{ statusText }}</el-tag>
					</div>

					<!-- 基本信息 - 使用带边框的Descriptions描述列表 -->
					<div class="base-info-section">
						<h4 class="section-title">基本信息</h4>
						<el-descriptions :column="2" border class="info-descriptions" size="medium">
							<el-descriptions-item label="入库编号" :span="1">
								<span class="info-value">{{ planData.docNum || '--' }}</span>
							</el-descriptions-item>
							<el-descriptions-item label="创建人" :span="1">
								<span class="info-value">{{ planData.createName || '--' }}</span>
							</el-descriptions-item>
							<el-descriptions-item label="创建时间" :span="1">
								<span class="info-value">{{ formatDate(planData.createTime) || '--' }}</span>
							</el-descriptions-item>
							<el-descriptions-item label="供货单位" :span="1">
								<span class="info-value">{{ planData.supplierName || '--' }}</span>
							</el-descriptions-item>
							<el-descriptions-item label="入库方式" :span="1">
								<span class="info-value">{{ getPutinTypeText(planData.putinTypeId) || '--' }}</span>
							</el-descriptions-item>
							<el-descriptions-item label="发票号码" :span="1">
								<span class="info-value">{{ planData.purcInvoCodg || '--' }}</span>
							</el-descriptions-item>
							<el-descriptions-item label="业务日期" :span="1">
								<span class="info-value">{{ formatDate(planData.busDate) || '--' }}</span>
							</el-descriptions-item>
							<el-descriptions-item label="备注" :span="2">
								<span class="info-value" :class="{'empty-text': !planData.remarks}">
									{{ planData.remarks || '暂无备注' }}
								</span>
							</el-descriptions-item>
							<el-descriptions-item label="驳回原因" :span="2" v-if="planData.lastRejectReason">
								<span class="reject-reason">{{ planData.lastRejectReason }}</span>
							</el-descriptions-item>
						</el-descriptions>
					</div>

					<!-- 药品明细表格 -->
					<div class="goods-table-section">
						<div class="section-header">
							<h4 class="section-title">药品明细</h4>
							<div class="total-info">
								<span class="total-item">成本合计：¥{{ calculateTotalCost() }}</span>
								<span class="total-item">零售合计：¥{{ calculateTotalSale() }}</span>
								<span class="total-item">共 {{ planData.list?.length || 0 }} 种药品</span>
							</div>
						</div>

						<el-table :data="planData.list" stripe border style="width: 100%" size="small"
							v-loading="loading">
							<el-table-column type="index" label="序号" width="60" align="center" />
							<el-table-column prop="drugName" label="药品名称" width="180" show-overflow-tooltip />
							<el-table-column prop="drugSpec" label="药品规格" width="160">
								<template slot-scope="scope">
									<el-tooltip :content="scope.row.drugSpec || '--'" placement="top"
										:disabled="!scope.row.drugSpec || scope.row.drugSpec.length <= 8">
										<span class="spec-text">
											{{ formatSpecText(scope.row.drugSpec) }}
										</span>
									</el-tooltip>
								</template>
							</el-table-column>
							<el-table-column prop="pacSpec" label="包装规格" width="160">
								<template slot-scope="scope">
									<el-tooltip :content="scope.row.pacSpec || '--'" placement="top"
										:disabled="!scope.row.pacSpec || scope.row.pacSpec.length <= 8">
										<span class="spec-text">
											{{ formatSpecText(scope.row.pacSpec) }}
										</span>
									</el-tooltip>
								</template>
							</el-table-column>

							<el-table-column label="数量" width="120" align="center">
								<template slot-scope="scope">
									{{ scope.row.purcCnt || 0 }}
									{{ scope.row.purcType === '1' ? (scope.row.minUnt || '') : (scope.row.unt || '') }}
								</template>
							</el-table-column>

							<el-table-column label="成本单价" width="120" align="right">
								<template slot-scope="scope">
									¥{{ Number(scope.row.purcPric || 0) }}
								</template>
							</el-table-column>

							<el-table-column label="零售单价" width="100" align="right">
								<template slot-scope="scope">
									¥{{ Number(scope.row.salePric || 0) }}
								</template>
							</el-table-column>

							<el-table-column label="成本金额" width="120" align="right">
								<template slot-scope="scope">
									<span style="color: #ff6b35; font-weight: bold;">
										¥{{ Number(scope.row.purcAmt || 0) }}
									</span>
								</template>
							</el-table-column>

							<el-table-column label="零售金额" width="120" align="right">
								<template slot-scope="scope">
									<span style="color: #67c23a; font-weight: bold;">
										¥{{ Number(scope.row.saleAmt || 0) }}
									</span>
								</template>
							</el-table-column>

							<el-table-column prop="manuLotnum" label="生产批号" width="120" />
							<el-table-column prop="prodentpName" label="生产厂家" width="180" show-overflow-tooltip />
							<el-table-column label="生产日期" width="100">
								<template slot-scope="scope">
									{{ formatDate(scope.row.manuDate) || '--' }}
								</template>
							</el-table-column>
							<el-table-column label="有效日期" width="100">
								<template slot-scope="scope">
									{{ formatDate(scope.row.expyDate) || '--' }}
								</template>
							</el-table-column>
							<el-table-column prop="remarks" label="备注" min-width="120" show-overflow-tooltip>
								<template slot-scope="scope">
									{{ scope.row.remarks || '--' }}
								</template>
							</el-table-column>
						</el-table>
					</div>

					<!-- 驳回原因输入框（审核模式显示） -->
			<!-- 		<div v-if="isExamMode" class="reject-reason-section">
						<h4 class="section-title">审核操作</h4>
						<el-form :model="auditForm" :rules="auditRules" ref="auditForm">
							<el-form-item label="驳回原因（驳回时必填）" prop="rejectReason">
								<el-input v-model="auditForm.rejectReason" type="textarea" :rows="3"
									placeholder="请输入驳回原因" :disabled="!showRejectReason" />
							</el-form-item>
						</el-form>
					</div> -->
				</div>

				<!-- 无数据状态 -->
				<div v-else class="no-data">
					<el-empty description="暂无数据" />
				</div>
			</div>

			<span slot="footer" class="dialog-footer">
				<!-- 查看模式 -->
				<template v-if="isViewMode">
					<el-button @click="handleClose">关闭</el-button>
				</template>

				<!-- 审核模式 -->
				<template v-if="isExamMode">
					<el-button @click="handleClose" :disabled="passLoading || rejectLoading">关闭</el-button>
					<!-- <el-button type="danger" @click="handleReject"
						:disabled="showRejectReason && !auditForm.rejectReason" :loading="rejectLoading">
						{{ examModeType === 'audit' ? '驳回' : '拒绝' }}
					</el-button> -->
					<el-button type="primary" @click="handlePass" :loading="passLoading">
						{{ getConfirmButtonText() }}
					</el-button>
				</template>

				<!-- 快捷调拨模式 -->
				<template v-if="isQuickTransferMode">
					<el-button @click="handleCancel">取消</el-button>
					<el-button type="primary" @click="handleQuickTransferConfirm" :loading="transferLoading">
						确认无误，快捷调拨
					</el-button>
				</template>
			</span>
		</el-dialog>
	</div>
</template>

<script>
	import {
		toDrugExamine,
		getDrugDetails,
		toXYFDrugExamine,
		getXYFDrugDetails,
	} from "@/api/PSI.js";

	export default {
	  name: 'StorePsiDetail',
	  props: {
	    visible: {
	      type: Boolean,
	      default: false
	    },
	    mode: {
	      type: String,
	      default: 'view',
	      validator: (value) => ['view', 'edit', 'audit', 'exam', 'quickTransfer'].includes(value)
	    },
	    planId: {
	      type: [String, Number],
	      default: null
	    }
	  },
		data() {
			return {
				dialogVisible: false,
				loading: false,
				passLoading: false,
				rejectLoading: false,
				hasLoaded: false,
				loadTimer: null,
				// 审核模式类型：submit-提交, audit-审核, pass-过单
				examModeType: 'submit',
				// 新增：当前库房状态
				currentWarehouse: 1, // 默认西药库
				// 当前处理的计划ID
				currentPlanId: null,
				
				// 计划数据
				planData: {
					id: null,
					docNum: '',
					state: 0,
					createName: '',
					createTime: '',
					busDate: '',
					remarks: '',
					supplierName: '',
					purcInvoCodg: '',
					putinTypeId: 1,
					lastRejectReason: '',
					list: []
				},
				transferLoading: false,

				// 使用内部数据来管理模式，避免直接修改prop
				internalMode: this.mode,
				internalIsQuickTransferMode: this.mode === 'quickTransfer',

				// 审核表单
				auditForm: {
					rejectReason: ''
				},

				// 审核表单验证规则
				auditRules: {
					rejectReason: [{
						required: true,
						message: '驳回原因不能为空',
						trigger: 'blur'
					}]
				},

				// 控制驳回原因显示
				showRejectReason: false
			}
		},
		computed: {
			// 使用内部数据来计算模式
			isViewMode() {
				return this.internalMode === 'view' && !this.internalIsQuickTransferMode;
			},
			isEditMode() {
				return this.internalMode === 'edit' && !this.internalIsQuickTransferMode;
			},
			isExamMode() {
				return this.internalMode === 'exam' && !this.internalIsQuickTransferMode;
			},

			// 修复：为计算属性添加setter
			isQuickTransferMode: {
				get() {
					return this.internalIsQuickTransferMode;
				},
				set(value) {
					this.internalIsQuickTransferMode = value;
					// 同步更新internalMode
					if (value) {
						this.internalMode = 'quickTransfer';
					} else if (this.internalMode === 'quickTransfer') {
						this.internalMode = 'view';
					}
				}
			},

			// 修改对话框标题逻辑
			dialogTitle() {
				// 快捷调拨模式优先
				if (this.isQuickTransferMode) {
					return `快捷调拨单 - ${this.planData.docNum || '--'}`;
				}

				// 审核模式
				if (this.isExamMode) {
					const baseTitle = `采购入库详情（${this.planData.docNum || '--'}）`;
					const modeTitles = {
						submit: `提交${baseTitle}`,
						audit: `审核${baseTitle}`,
						pass: `过单${baseTitle}`,
					};
					return modeTitles[this.examModeType] || baseTitle;
				}

				// 查看和编辑模式
				const baseTitle = `采购入库详情（${this.planData.docNum || '--'}）`;
				const modeTitles = {
					view: `查看${baseTitle}`,
					edit: `修改${baseTitle}`,
				};
				return modeTitles[this.internalMode] || baseTitle;
			},

			// 修改状态文本显示
			statusText() {
				// 快捷调拨模式优先
				if (this.isQuickTransferMode) {
					return '快捷调拨';
				}

				// 正常状态显示
				const textMap = {
					0: '待提交',
					2: '待审核',
					4: '待过单',
					5: '已过单',
					6: '已冲销'
				};
				return textMap[this.planData.state] || '未知状态';
			},

			// 修改状态标签类型
			statusTagType() {
				if (this.isQuickTransferMode) {
					return 'success';
				}

				const typeMap = {
					0: 'info',
					2: 'primary',
					4: 'warning',
					5: 'success',
					6: 'danger'
				};
				return typeMap[this.planData.state] || 'info';
			}
		},
		watch: {
			// 修复：监听prop变化，更新内部数据
			mode: {
				immediate: true,
				handler(newMode) {
					this.internalMode = newMode;
					this.internalIsQuickTransferMode = newMode === 'quickTransfer';
				}
			},
			visible: {
				immediate: true,
				handler(newVal) {
					this.dialogVisible = newVal;
					if (newVal && this.planId && this.planId !== this.currentPlanId) {
						this.currentPlanId = this.planId;
						this.loadPlanData(this.planId);
					}
				}
			},
			planId: {
				handler(newVal, oldVal) {
					if (this.dialogVisible && newVal && newVal !== oldVal && newVal !== this.currentPlanId) {
						this.currentPlanId = newVal;
						this.loadPlanData(newVal);
					}
				}
			},
			dialogVisible: {
				handler(newVal) {
					this.$emit('update:visible', newVal);
					if (!newVal) {
						this.resetForm();
					}
				}
			}
		},
		methods: {
			// 强制设置模式（供父组件调用）
			setDialogMode(mode, examModeType = null) {
				this.internalMode = mode;
				this.internalIsQuickTransferMode = mode === 'quickTransfer';

				if (examModeType) {
					this.examModeType = examModeType;
				}

				// 通知父组件模式变化
				this.$emit('mode-change', mode);
			},

			// 获取审核模式标题
			getExamModeTitle() {
				const titles = {
					submit: '提交采购入库',
					audit: '审核采购入库',
					pass: '过单采购入库'
				};
				return titles[this.examModeType] || '操作采购入库';
			},

			// 处理快捷调拨确认
			handleQuickTransferConfirm() {
				this.$emit('quick-transfer-confirm');
			},

			// 处理取消
			handleCancel() {
				this.handleClose();
			},

			// 关闭方法，重置所有模式
			handleClose() {
				this.internalMode = 'view';
				this.internalIsQuickTransferMode = false;
				this.examModeType = 'submit';
				this.dialogVisible = false;
				this.$emit('close');
			},

			// 根据单据状态自动设置审核模式类型
			autoSetExamModeType() {
				if (!this.planData.state) return;

				const state = this.planData.state;

				if (state === 0) {
					this.examModeType = 'submit';
				} else if (state === 2) {
					this.examModeType = 'audit';
				} else if (state === 4) {
					this.examModeType = 'pass';
				} else {
					this.examModeType = this.examModeType || 'submit';
				}
			},

			// 获取确认按钮文本
			getConfirmButtonText() {
				const texts = {
					submit: '确认提交',
					audit: '审核通过',
					pass: '确认过单'
				};
				return texts[this.examModeType] || '确认';
			},

			// 设置审核模式类型（供父组件调用）
			setExamModeType(type) {
				this.examModeType = type;
			},

			// 获取第一个药品名称
			getFirstDrugName() {
				if (this.planData.list && this.planData.list.length > 0) {
					return this.planData.list[0].drugName;
				}
				return null;
			},

			// 计算成本合计
			calculateTotalCost() {
				if (!this.planData.list || !Array.isArray(this.planData.list)) {
					return '0.00';
				}
				const total = this.planData.list.reduce((sum, item) => {
					return sum + (Number(item.purcAmt) || 0);
				}, 0);
				return total;
			},

			// 计算零售合计
			calculateTotalSale() {
				if (!this.planData.list || !Array.isArray(this.planData.list)) {
					return '0.00';
				}
				const total = this.planData.list.reduce((sum, item) => {
					return sum + (Number(item.saleAmt) || 0);
				}, 0);
				return total;
			},

			// 格式化日期
			formatDate(dateString) {
				if (!dateString || dateString === '--') return '--';
				try {
					const date = new Date(dateString);
					const year = date.getFullYear();
					const month = String(date.getMonth() + 1).padStart(2, '0');
					const day = String(date.getDate()).padStart(2, '0');
					return `${year}-${month}-${day}`;
				} catch (error) {
					return '--';
				}
			},

			// 获取入库方式文本
			getPutinTypeText(typeId) {
				const typeMap = {
					1: '采购入库',
					2: '其他入库',
					3: '赠送入库',
					4: '盘点入库',
					5: '系统切换入库'
				};
				return typeMap[typeId] || '未知';
			},

			// 新增：设置库房信息（供父组件调用）
			  setWarehouse(warehouse) {
			    this.currentWarehouse = warehouse;
			  },
			  
			  // 修改：根据库房选择详情接口
			  async loadPlanData(planId) {
			    try {
			      this.loading = true;
			      
			      // 根据当前库房选择详情接口
			      const detailApi = this.currentWarehouse == 3 ? getXYFDrugDetails : getDrugDetails;
			      
			      const response = await detailApi({
			        id: planId
			      });
			
			      if (response.code === 200) {
			        const responseData = response.data || {};
			        if (!responseData.list) {
			          responseData.list = [];
			        }
			
			        this.setPlanData(responseData);
			      }
			    } catch (error) {
			      console.error('加载详情失败:', error);
			    } finally {
			      this.loading = false;
			    }
			  },

			// 手动加载数据的方法（供父组件调用）
			loadDataManually(planId) {
				if (planId) {
					this.currentPlanId = planId;
					this.loadPlanData(planId);
				}
			},

			setPlanData(data) {
				if (!data) {
					this.resetPlanData();
					return;
				}

				const deepClone = (obj) => {
					if (!obj || typeof obj !== 'object') return obj;
					if (obj instanceof Date) return new Date(obj);
					if (obj instanceof RegExp) return new RegExp(obj);

					const clone = Array.isArray(obj) ? [] : {};
					for (const key in obj) {
						if (obj.hasOwnProperty(key)) {
							clone[key] = deepClone(obj[key]);
						}
					}
					return clone;
				};

				const ensuredData = deepClone(data);

				if (!ensuredData.hasOwnProperty('drugSpec')) {
					ensuredData.drugSpec = '';
				}

				ensuredData.list = (ensuredData.list || []).map(item => {
					const newItem = {
						...item
					};
					if (!newItem.hasOwnProperty('drugSpec')) {
						newItem.drugSpec = item.drugSpec || item.drugSpecifications || '';
					}
					return newItem;
				});

				this.planData = {
					id: ensuredData.id || null,
					docNum: ensuredData.docNum || '',
					state: ensuredData.state || 0,
					createName: ensuredData.createName || '',
					createTime: ensuredData.createTime || '',
					busDate: ensuredData.busDate || '',
					remarks: ensuredData.remarks || '',
					drugSpec: ensuredData.drugSpec || '',
					supplierName: ensuredData.supplierName || '',
					purcInvoCodg: ensuredData.purcInvoCodg || '',
					putinTypeId: ensuredData.putinTypeId || 1,
					lastRejectReason: ensuredData.lastRejectReason || '',
					list: ensuredData.list || []
				};

				this.hasLoaded = true;

				if (this.internalMode === 'exam') {
					this.autoSetExamModeType();
				}
			},

			// 格式化规格文本
			formatSpecText(text) {
				if (!text) return '--';

				if (text.length > 8) {
					return text.substring(0, 8) + '...';
				}

				if (text.length > 10) {
					return text.substring(0, 10) + '...';
				}

				return text;
			},

			// 重置计划数据
			resetPlanData() {
				this.planData = {
					id: null,
					docNum: '',
					state: 0,
					createName: '',
					createTime: '',
					busDate: '',
					remarks: '',
					supplierName: '',
					purcInvoCodg: '',
					putinTypeId: 1,
					lastRejectReason: '',
					list: []
				};
				this.hasLoaded = false;
			},

			// 处理通过（确认提交/审核通过/过单）
			handlePass() {
				const confirmTexts = {
					submit: '确定要提交此采购入库吗？提交后将进入审核流程',
					audit: '确定要审核通过此采购入库吗？',
					pass: '确定要过单此采购入库吗？过单后将完成入库操作'
				};

				this.$confirm(confirmTexts[this.examModeType] || '确定要执行此操作吗？', '提示', {
					type: 'warning',
					confirmButtonText: '确定',
					cancelButtonText: '取消'
				}).then(() => {
					this.submitPass();
				}).catch(() => {});
			},

			// 修改：提交通过操作，根据库房选择接口
			  async submitPass() {
			    const documentId = this.currentPlanId;
			    if (!documentId) {
			      this.$message.error('无法获取有效的单据ID，请刷新页面重试');
			      return;
			    }
			
			    try {
			      this.passLoading = true;
			
			      const targetStateMap = {
			        submit: 2,
			        audit: 4,
			        pass: 5
			      };
			
			      const targetState = targetStateMap[this.examModeType] || 2;
			
			      const submitData = {
			        id: documentId,
			        state: targetState
			      };
			
			      // 根据当前库房选择审核接口
			      const examineApi = this.currentWarehouse == 3 ? toXYFDrugExamine : toDrugExamine;
			      
			      const response = await examineApi(submitData);
			
			      if (response.code === 200) {
			        this.$message.success('操作成功');
			
			        this.$emit('operation-success', {
			          type: this.examModeType,
			          state: targetState,
			          planId: documentId,
			          message: '操作成功'
			        });
			
			        this.handleClose();
			      } else {
			        this.$message.error(response.message || '操作失败');
			      }
			    } catch (error) {
			      console.error('操作失败:', error);
			      this.$message.error('操作失败');
			    } finally {
			      this.passLoading = false;
			    }
			  },
			  
			  // 修改：提交驳回操作，根据库房选择接口
			  async submitReject() {
			    const documentId = this.currentPlanId;
			    if (!documentId) {
			      this.$message.error('无法获取有效的单据ID，请刷新页面重试');
			      return;
			    }
			
			    try {
			      this.rejectLoading = true;
			
			      const rejectStateMap = {
			        submit: 0,
			        audit: 2,
			        pass: 4
			      };
			
			      const rejectState = rejectStateMap[this.examModeType] || 1;
			
			      const submitData = {
			        id: documentId,
			        state: rejectState,
			        rejectReason: this.auditForm.rejectReason
			      };
			
			      // 根据当前库房选择审核接口
			      const examineApi = this.currentWarehouse == 3 ? toXYFDrugExamine : toDrugExamine;
			      
			      const response = await examineApi(submitData);
			
			      if (response.code === 200) {
			        this.$message.success('驳回成功');
			
			        this.$emit('operation-success', {
			          type: 'reject',
			          state: rejectState,
			          planId: documentId,
			          message: '驳回成功'
			        });
			
			        this.handleClose();
			      } else {
			        this.$message.error(response.message || '驳回失败');
			      }
			    } catch (error) {
			      console.error('驳回操作失败:', error);
			      this.$message.error('驳回失败');
			    } finally {
			      this.rejectLoading = false;
			    }
			  },

			// 处理驳回
			handleReject() {
				if (!this.showRejectReason) {
					this.showRejectReason = true;
					return;
				}

				this.$refs.auditForm.validate((valid) => {
					if (valid) {
						const rejectTexts = {
							submit: '确定要驳回此采购入库的提交吗？',
							audit: '确定要驳回此采购入库的审核吗？',
							pass: '确定要驳回此采购入库的过单吗？'
						};

						this.$confirm(rejectTexts[this.examModeType] || '确定要驳回吗？', '提示', {
							type: 'warning',
							confirmButtonText: '确定驳回',
							cancelButtonText: '取消',
						}).then(() => {
							this.submitReject();
						}).catch(() => {});
					}
				});
			},

			// 修改重置表单方法
			  resetForm() {
			    this.auditForm.rejectReason = '';
			    this.showRejectReason = false;
			    this.passLoading = false;
			    this.rejectLoading = false;
			    this.loading = false;
			    this.examModeType = 'submit';
			    // 重置内部模式状态
			    this.internalMode = 'view';
			    this.internalIsQuickTransferMode = false;
			    // 重置库房状态
			    this.currentWarehouse = 1;
			  }
		}
	}
</script>

<style lang="less" scoped>
	// 详情模态框样式
	.purchase-detail-dialog {
		/deep/ .el-dialog {
			max-height: 90vh;
			display: flex;
			flex-direction: column;

			.el-dialog__body {
				flex: 1;
				overflow-y: auto;
				padding: 20px;
			}

			&__header {
				background: linear-gradient(135deg, #1a6be0 0%, #409EFF 100%);
				padding: 12px 20px;
				border-bottom: 1px solid #e6e6e6;
				display: flex;

				.el-dialog__title {
					color: #fff;
					font-size: 16px;
					font-weight: bold;
				}
			}

			.el-dialog__headerbtn .el-dialog__close {
				color: #fff;
			}

			.el-dialog__headerbtn .el-dialog__close:hover {
				color: #f80000;
			}
		}

		.detail-content {
			.loading-container {
				padding: 20px 0;

				.custom-skeleton {
					.skeleton-header {
						display: flex;
						justify-content: space-between;
						align-items: center;
						margin-bottom: 24px;
						padding-bottom: 16px;
						border-bottom: 1px solid #e6e6e6;
					}

					.skeleton-section {
						margin-bottom: 24px;
					}

					.skeleton-descriptions {
						/deep/ .el-descriptions__body {
							background: #f5f7fa;

							.el-descriptions-item__cell {
								padding: 12px 16px;
								background: #fff;

								&.el-descriptions-item__label {
									background: #f8f9fa;
								}
							}
						}
					}
				}
			}

			.no-data {
				padding: 40px 0;
				text-align: center;
			}

			.header-info {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-bottom: 24px;
				padding-bottom: 16px;
				border-bottom: 1px solid #e6e6e6;

				.drug-name {
					font-size: 18px;
					font-weight: 600;
					color: #303133;
				}
			}

			.base-info-section {
				margin-bottom: 24px;

				.section-title {
					font-size: 16px;
					font-weight: 600;
					color: #303133;
					margin-bottom: 16px;
					padding-left: 8px;
					border-left: 4px solid #409EFF;
				}

				.info-descriptions {
					/deep/ .el-descriptions {
						width: 100%;
						border-radius: 6px;
						overflow: hidden;
						box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

						.el-descriptions__header {
							display: none;
						}

						.el-descriptions__body {
							background: #fff;

							.el-descriptions__table {
								width: 100%;
								border-collapse: collapse;

								tbody tr:last-child td {
									border-bottom: 1px solid #e8e8e8;
								}

								.el-descriptions-item__cell {
									padding: 12px 16px;

									&.el-descriptions-item__label {
										background: #f8f9fa;
										font-weight: 600;
										color: #495057;
										width: 120px;
										border-right: 1px solid #e8e8e8;
									}

									&.el-descriptions-item__content {
										background: #fff;
										color: #212529;

										.info-value {
											font-weight: 500;

											&.empty-text {
												color: #6c757d;
												font-style: italic;
											}
										}

										.reject-reason {
											color: #f56c6c;
											font-weight: 500;
											background: #fff5f5;
											padding: 4px 8px;
											border-radius: 4px;
											display: inline-block;
										}
									}
								}
							}
						}
					}
				}
			}

			.goods-table-section {
				margin-bottom: 24px;

				.section-header {
					display: flex;
					justify-content: space-between;
					align-items: center;
					margin-bottom: 16px;

					.section-title {
						font-size: 16px;
						font-weight: 600;
						color: #303133;
						margin: 0;
						padding-left: 8px;
						border-left: 4px solid #67C23A;
					}

					.total-info {
						display: flex;
						gap: 20px;

						.total-item {
							font-size: 14px;
							color: #606266;

							&:first-child {
								color: #ff6b35;
								font-weight: 500;
							}

							&:nth-child(2) {
								color: #67c23a;
								font-weight: 500;
							}
						}
					}
				}

				/deep/ .el-table {
					font-size: 13px;

					.el-table__header-wrapper {
						th {
							background-color: #f5f7fa;
							font-weight: 600;
							color: #606266;
						}
					}

					.el-table__body-wrapper {
						tr:hover>td {
							background-color: #f0f7ff;
						}
					}
				}
			}

			.reject-reason-section {
				margin-top: 24px;
				padding-top: 16px;
				border-top: 1px solid #e6e6e6;

				.section-title {
					font-size: 16px;
					font-weight: 600;
					color: #303133;
					margin-bottom: 16px;
					padding-left: 8px;
					border-left: 4px solid #F56C6C;
				}

				/deep/ .el-form-item {
					margin-bottom: 0;

					.el-form-item__label {
						font-size: 14px;
						font-weight: 500;
						color: #606266;
					}

					.el-textarea {
						.el-textarea__inner {
							resize: vertical;
							min-height: 80px;
						}
					}
				}
			}
		}

		.dialog-footer {
			.el-button {
				min-width: 100px;

				&.is-loading {
					opacity: 0.6;
					cursor: not-allowed;
				}
			}
		}
	}

	// 响应式设计
	@media screen and (max-width: 1200px) {
		.purchase-detail-dialog {
			/deep/ .el-dialog {
				width: 90% !important;
			}
		}
	}

	@media screen and (max-width: 768px) {
		.purchase-detail-dialog {
			/deep/ .el-dialog {
				width: 95% !important;
				margin-top: 2vh !important;
			}

			.detail-content {
				.header-info {
					flex-direction: column;
					align-items: flex-start;
					gap: 12px;
				}

				.base-info-section {
					.info-descriptions {
						/deep/ .el-descriptions {
							.el-descriptions__body {
								.el-descriptions__table {
									.el-descriptions-item__cell {
										display: block;
										width: 100%;
										border-bottom: 1px solid #e8e8e8;

										&.el-descriptions-item__label {
											width: 100%;
											border-right: none;
											border-bottom: none;
											font-weight: 600;
											background: #f8f9fa;
										}

										&.el-descriptions-item__content {
											padding-left: 20px;
										}
									}
								}
							}
						}
					}
				}

				.goods-table-section {
					.section-header {
						flex-direction: column;
						align-items: flex-start;
						gap: 12px;

						.total-info {
							flex-wrap: wrap;
							gap: 12px;
						}
					}

					/deep/ .el-table {
						.el-table__body-wrapper {
							overflow-x: auto;
						}
					}
				}
			}
		}
	}

	@media screen and (max-width: 480px) {
		.purchase-detail-dialog {
			.detail-content {
				padding: 16px;

				.header-info {
					.drug-name {
						font-size: 16px;
					}
				}

				.base-info-section,
				.goods-table-section,
				.reject-reason-section {
					.section-title {
						font-size: 15px;
					}
				}
			}

			.dialog-footer {
				.el-button {
					min-width: 80px;
					font-size: 13px;

					&+.el-button {
						margin-left: 8px;
					}
				}
			}
		}
	}
</style>